import * as React from 'react';
import { WorkspaceButton } from '@/components/ui/workspace';
import { useLibraryTreeQuery } from '@/lib/api/requirement-library';
import {
  errorMessage,
  usePatchProposal,
  type KbProduct,
  type KbProposal,
} from '@/lib/api/vendor-knowledge';
import { cn } from '@/lib/utils/cn';
import { FormError, Linkish, smallControl, textareaControl } from '../kb-bits';
import { DECLINE_REASONS, KIND_LABEL } from '../kb-logic';

/** What a confirm did, shown at the top of To review until dismissed (E13-F06 §3). */
export interface ReviewResult {
  confirmed: Array<{ id: string; number: string }>;
  becameCitation: Array<{ id: string; itemNumber: string | null }>;
  accepted: Array<{ itemNumber: string | null }>;
  skipped: Array<{ id: string; reason: string; label: string }>;
  failed: string[];
}

export function emptyResult(): ReviewResult {
  return { confirmed: [], becameCitation: [], accepted: [], skipped: [], failed: [] };
}

/** A pill to pick a reason with (`.fchip`). */
export function FChip({
  children,
  onClick,
  pressed,
}: {
  children: React.ReactNode;
  onClick: () => void;
  pressed?: boolean;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 items-center rounded-full border px-2.5 text-meta font-medium',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-amber-600',
        pressed
          ? 'border-ink-900 bg-ink-900 text-paper-0'
          : 'border-ink-200 bg-paper-0 text-ink-700 hover:border-ink-300',
      )}
    >
      {children}
    </button>
  );
}

/** "Why?" and the decline reasons (`.decline`); Other asks for a few words. */
export function DeclineChooser({
  onDecline,
  onCancel,
  pending,
  label = 'Why?',
}: {
  onDecline: (code: string, text?: string) => void;
  onCancel: () => void;
  pending?: boolean;
  label?: string;
}) {
  const [other, setOther] = React.useState<string | null>(null);
  return (
    <div
      role="group"
      aria-label="Decline reasons"
      className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-dashed border-ink-100 pt-2"
    >
      <span className="text-meta text-ink-500">{label}</span>
      {DECLINE_REASONS.map((r) =>
        r.code === 'other' ? (
          <FChip key={r.code} pressed={other !== null} onClick={() => setOther(other ?? '')}>
            {r.label}
          </FChip>
        ) : (
          <FChip key={r.code} onClick={() => !pending && onDecline(r.code)}>
            {r.label}
          </FChip>
        ),
      )}
      {other !== null ? (
        <span className="inline-flex items-center gap-1.5">
          <input
            autoFocus
            className={`${smallControl} w-[220px]`}
            value={other}
            onChange={(e) => setOther(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && other.trim()) onDecline('other', other.trim());
            }}
            placeholder="Say why"
            aria-label="Why decline it?"
            autoComplete="off"
          />
          <WorkspaceButton
            variant="destructive"
            disabled={!other.trim() || pending}
            onClick={() => onDecline('other', other.trim())}
          >
            Decline
          </WorkspaceButton>
        </span>
      ) : null}
      <Linkish onClick={onCancel}>Cancel</Linkish>
    </div>
  );
}

/**
 * A proposal's statement, edited in place (`.kb-stmt-in`). It is saved when
 * the field is left; the server checks its numbers and names (rules 11, 13).
 */
export function StatementInput({
  proposal,
  value,
  onChange,
  onSaved,
  error,
  setError,
}: {
  proposal: KbProposal;
  value: string;
  onChange: (v: string) => void;
  onSaved: () => void;
  error: string | null;
  setError: (e: string | null) => void;
}) {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  const patch = usePatchProposal();
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  const save = () => {
    const clean = value.trim().replace(/\s+/g, ' ');
    if (clean === (proposal.statement ?? '')) return;
    if (!clean) {
      setError('Write the statement.');
      return;
    }
    patch.mutate(
      { id: proposal.id, body: { statement: clean } },
      { onSuccess: onSaved, onError: (err) => setError(errorMessage(err)) },
    );
  };
  return (
    <>
      <textarea
        ref={ref}
        rows={1}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (error) setError(null);
        }}
        onBlur={save}
        placeholder="Write the statement: what this passage says about the product"
        aria-label="Statement, editable"
        title="Edit the statement if it needs it. The quote below can’t be edited."
        className={cn(
          '-ml-1.5 block min-h-[26px] w-full resize-none overflow-hidden rounded-md border border-transparent bg-transparent px-1.5 py-0.5 text-body font-medium text-ink-900',
          'hover:border-ink-200 hover:bg-paper-0 focus:bg-paper-0 focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent-amber-600',
        )}
      />
      {error ? <FormError>{error}</FormError> : null}
    </>
  );
}

/** The library's modules, by category, for filing a proposal. */
export function useModuleOptions() {
  const tree = useLibraryTreeQuery();
  return (tree.data?.categories ?? [])
    .filter((c) => c.status === 'active')
    .map((c) => ({
      category: c.name,
      modules: c.modules
        .filter((m) => m.status === 'active')
        .map((m) => ({ id: m.id, name: m.name })),
    }))
    .filter((c) => c.modules.length > 0);
}

/**
 * Kind, product, module and process area of a proposal, changed in place
 * (prototype `.kb-pickers`). Saved with one PATCH on Done.
 */
export function ClassifyPickers({
  proposal,
  products,
  onDone,
}: {
  proposal: KbProposal;
  products: KbProduct[];
  onDone: () => void;
}) {
  const modules = useModuleOptions();
  const patch = usePatchProposal();
  const [kind, setKind] = React.useState(proposal.kind);
  const [productId, setProductId] = React.useState(proposal.product_id ?? '');
  const [moduleId, setModuleId] = React.useState(proposal.library_module_id ?? '');
  const [area, setArea] = React.useState(proposal.process_area ?? '');
  const [error, setError] = React.useState<string | null>(null);
  const done = () => {
    const body: Record<string, unknown> = {};
    if (kind !== proposal.kind) body['kind'] = kind;
    if (productId !== (proposal.product_id ?? '')) body['product_id'] = productId || null;
    if (moduleId !== (proposal.library_module_id ?? ''))
      body['library_module_id'] = moduleId || null;
    if (area.trim() !== (proposal.process_area ?? '')) body['process_area'] = area.trim() || null;
    if (Object.keys(body).length === 0) return onDone();
    patch.mutate(
      { id: proposal.id, body },
      { onSuccess: onDone, onError: (err) => setError(errorMessage(err)) },
    );
  };
  const label = 'mb-1 block text-meta font-medium text-ink-700';
  return (
    <div className="ml-7 mt-2.5 flex flex-wrap items-end gap-2.5 rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5">
      <label className="flex flex-col">
        <span className={label}>Kind</span>
        <select
          className={`${smallControl} pr-7`}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          {Object.entries(KIND_LABEL).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className={label}>Product</span>
        <select
          className={`${smallControl} pr-7`}
          value={productId}
          onChange={(e) => setProductId(e.target.value)}
        >
          <option value="">All products</option>
          {products
            .filter((p) => p.status === 'active')
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className={label}>Module</span>
        <select
          className={`${smallControl} max-w-[220px] pr-7`}
          value={moduleId}
          onChange={(e) => setModuleId(e.target.value)}
        >
          <option value="">{proposal.module_label ? proposal.module_label : 'No module'}</option>
          {modules.map((c) => (
            <optgroup key={c.category} label={c.category}>
              {c.modules.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className={label}>Process area</span>
        <input
          className={`${smallControl} w-[180px]`}
          value={area}
          onChange={(e) => setArea(e.target.value)}
          autoComplete="off"
        />
      </label>
      <Linkish className="mb-1.5" onClick={done} disabled={patch.isPending}>
        Done
      </Linkish>
      {error ? <FormError>{error}</FormError> : null}
    </div>
  );
}

/** A plain textarea for a reason or a corrected statement. */
export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea rows={2} {...props} className={cn(textareaControl, props.className)} />;
}
